import { useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client/react';
import { Monitor, Trash2, Loader2, FolderInput } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  cn,
} from '@scio3/ui';
import { DEVICES_QUERY, UNPAIR_DEVICES, MOVE_TO_GROUP } from '@scio3/graphql-client';
import { ON_HOLD_DEVICE_FOLDER } from '@scio3/shared-types';
import { toast } from 'sonner';
import { useConfirm } from '@/components/shared';
import { useTeamStore } from '@/stores/team-store';
import { FREE_PLAN_MAX_DEVICES, isUnsupportedFreePlanDevice } from '../utils/billing-constants';

const ONHOLD_PATH = `/${ON_HOLD_DEVICE_FOLDER}`;

const ITEMS_PER_PAGE = 10;

type ScreenManagementDialogProps = {
  open: boolean;
  onClose: () => void;
  /** Current total number of paired screens */
  totalPairedScreens: number;
  subAccountId?: string;
  teamId?: string;
  /**
   * SP53 — i18n key for the dialog's body message. Defaults to `screenManagementModal1`
   * (decrease-screen-count flow). Set to `screenManagementModalFreePlan` when opened
   * from a Free-plan downgrade error so the user sees the Free-plan-specific guidance.
   */
  messageKey?: string;
};

interface DeviceNode {
  _id: string;
  deviceName?: string;
  path?: string;
  // #1140 — fields needed to evaluate the Free-plan unsupported-device
  // predicate (scio1 parity, see billing-constants.isUnsupportedFreePlanDevice).
  os?: string;
  device?: string;
  model?: string;
  typeBuild?: string;
  manufacturer?: string;
}

interface DevicesResponse {
  devices: {
    page: {
      edges: Array<{ node: DeviceNode }>;
      pageInfo: { hasNextPage: boolean; endCursor: string };
    };
    pageData?: { total?: number };
  };
}

/**
 * Screen Management dialog matching scio1's screenManagementModal.
 * Shows when user tries to decrease subscription screen count.
 * Lists paired devices with delete (unpair) buttons.
 */
export function ScreenManagementDialog({
  open,
  onClose,
  totalPairedScreens,
  teamId,
  messageKey = 'screenManagementModal1',
}: ScreenManagementDialogProps) {
  const { t } = useTranslation();
  const activeTeamId = useTeamStore((s) => s.activeTeamId);
  const [afterCursor, setAfterCursor] = useState<string | undefined>(undefined);
  const [currentPage, setCurrentPage] = useState(1);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [movingId, setMovingId] = useState<string | null>(null);
  const confirm = useConfirm();
  // Store cursors for each page so we can navigate back
  const cursorMap = useRef<Record<number, string | undefined>>({ 1: undefined });

  const { data, loading, refetch } = useQuery<DevicesResponse>(DEVICES_QUERY, {
    variables: {
      first: ITEMS_PER_PAGE,
      after: afterCursor,
      // SP53 — exclude OnHold devices: scio1 parity. They don't consume a license,
      // so they must not be counted toward the Free-plan limit nor displayed here.
      query: { teamId, feature: { isOnHold_ne: true } },
    },
    skip: !open,
    fetchPolicy: 'network-only',
  });

  const [unpairDevices] = useMutation(UNPAIR_DEVICES, {
    refetchQueries: ['Devices', 'GetTotalScreensCount'],
  });

  // SP53 — move device to OnHold Devices folder (does not consume a license).
  const [moveToGroup] = useMutation(MOVE_TO_GROUP, {
    refetchQueries: ['Devices', 'GetTotalScreensCount'],
  });

  const allDevices = data?.devices?.page?.edges?.map((e) => e.node) ?? [];
  const hasNextPage = data?.devices?.page?.pageInfo?.hasNextPage ?? false;
  const endCursor = data?.devices?.page?.pageInfo?.endCursor;
  // SP53 — DEVICES_QUERY returns totalCount under `pageData.total`, NOT `page.totalCount`.
  // Fall back to the prop, or to the loaded edges length if neither is available.
  const totalCount = data?.devices?.pageData?.total ?? (totalPairedScreens || allDevices.length);

  // #1140 — Free-plan downgrade two-tier rule (scio1 parity,
  // screenManagementModal.js:107-112):
  //   - total > FREE_PLAN_MAX_DEVICES (3) → user must reduce screen count first;
  //     show ALL devices so they can delete any of them.
  //   - total ≤ FREE_PLAN_MAX_DEVICES → count is fine, only platform compatibility
  //     matters; show ONLY unsupported devices.
  const isFreePlanMode = messageKey === 'screenManagementModalFreePlan';
  const useUnsupportedFilter = isFreePlanMode && totalCount <= FREE_PLAN_MAX_DEVICES;
  const devices = useUnsupportedFilter
    ? allDevices.filter(isUnsupportedFreePlanDevice)
    : allDevices;

  // Hide pagination when the unsupported-only filter is active — post-filter
  // list is small (typically 0-3 devices) and server-side pagination counts
  // are misleading once filtered client-side.
  const totalPages = useUnsupportedFilter ? 1 : Math.max(1, Math.ceil(totalCount / ITEMS_PER_PAGE));

  // Store the endCursor for the next page when data loads
  if (endCursor && hasNextPage) {
    cursorMap.current[currentPage + 1] = endCursor;
  }

  const goToPage = useCallback((page: number) => {
    setCurrentPage(page);
    setAfterCursor(cursorMap.current[page]);
  }, []);

  const handleDelete = useCallback(
    async (device: DeviceNode) => {
      const result = await confirm({
        title: t('Unpair device'),
        description: t('Are you sure you want to unpair this device?'),
        confirm: { label: t('Unpair'), variant: 'destructive' },
      });
      if (result !== 'confirm') return;

      setRemovingId(device._id);
      try {
        await unpairDevices({
          variables: {
            teamId,
            payload: { deviceIds: [device._id] },
          },
        });
        toast.success(t('Device unpaired successfully'));
        refetch();
      } catch {
        toast.error(t('Failed to unpair device'));
      } finally {
        setRemovingId(null);
      }
    },
    [unpairDevices, teamId, refetch, t, confirm],
  );

  // SP53 — move a device to OnHold Devices folder (paused, no license consumed).
  const handleMoveToOnHold = useCallback(
    async (device: DeviceNode) => {
      const result = await confirm({
        title: t('Move to OnHold Devices'),
        description: t(
          'Do you want to move this device to the OnHold Devices folder? The screen will display blank content until moved back.',
        ),
        confirm: { label: t('Move to OnHold') },
      });
      if (result !== 'confirm') return;

      setMovingId(device._id);
      try {
        await moveToGroup({
          variables: {
            teamId: activeTeamId,
            payload: { type: 'DEVICE', newPath: ONHOLD_PATH, ids: [device._id] },
          },
        });
        toast.success(t('Device moved to OnHold'));
        refetch();
      } catch {
        toast.error(t('Failed to move device'));
      } finally {
        setMovingId(null);
      }
    },
    [moveToGroup, activeTeamId, refetch, t, confirm],
  );

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent
        className="max-h-[85vh] flex flex-col overflow-hidden sm:max-w-lg"
        data-testid="screen-management-dialog"
      >
        <DialogHeader>
          <DialogTitle>{t('Screen Management')}</DialogTitle>
        </DialogHeader>

        {/* Info message — defaults to decrease-count message; SP53 passes a Free-plan-specific key */}
        <div className="text-center text-sm text-muted-foreground px-4 py-2">
          {t(messageKey, { screenNumber: String(totalCount) })}
        </div>

        {/* Device list */}
        <div className="flex-1 overflow-y-auto px-4">
          {loading && devices.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 text-sm font-medium text-muted-foreground">
                    {t('Device Name')}
                  </th>
                  <th className="w-24" />
                </tr>
              </thead>
              <tbody>
                {devices.map((device) => {
                  const busy = removingId === device._id || movingId === device._id;
                  const isInOnHold = device.path === ONHOLD_PATH;
                  return (
                    <tr
                      key={device._id}
                      data-testid="screen-management-row"
                      className="border-b border-border"
                    >
                      <td className="py-3 text-sm">
                        <div className="flex items-center gap-2">
                          <Monitor className="h-4 w-4 text-muted-foreground shrink-0" />
                          <span>
                            {device.path ? `${device.path}/` : ''}
                            {device.deviceName ?? device._id}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            data-testid="screen-management-unpair-btn"
                            variant="outline"
                            size="icon-sm"
                            onClick={() => handleDelete(device)}
                            disabled={busy}
                            aria-label={t('Unpair device')}
                            className={cn(
                              'shrink-0',
                              busy ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
                            )}
                          >
                            {removingId === device._id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </Button>
                          {/* SP53 — Move to OnHold Devices folder. Hidden if device already there. */}
                          {!isInOnHold && (
                            <Button
                              data-testid="screen-management-onhold-btn"
                              variant="outline"
                              size="icon-sm"
                              onClick={() => handleMoveToOnHold(device)}
                              disabled={busy}
                              aria-label={t('Move to OnHold')}
                              className={cn(
                                'shrink-0',
                                busy ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
                              )}
                            >
                              {movingId === device._id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <FolderInput className="h-4 w-4" />
                              )}
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}

                {devices.length === 0 && !loading && (
                  <tr>
                    <td colSpan={2} className="py-6 text-center text-sm text-muted-foreground">
                      {t('No devices found')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-end gap-1 py-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage <= 1}
                className={cn(
                  currentPage <= 1 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
                )}
              >
                &lt;
              </Button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <Button
                  key={page}
                  variant={page === currentPage ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => goToPage(page)}
                  disabled={page > currentPage && !cursorMap.current[page]}
                  className={cn(
                    page > currentPage && !cursorMap.current[page]
                      ? 'opacity-50 cursor-not-allowed'
                      : 'cursor-pointer',
                    'min-w-8',
                  )}
                >
                  {page}
                </Button>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() => goToPage(currentPage + 1)}
                disabled={!hasNextPage}
                className={cn(!hasNextPage ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer')}
              >
                &gt;
              </Button>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            data-testid="screen-management-close-btn"
            variant="outline"
            onClick={onClose}
            className="cursor-pointer"
          >
            {t('Close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
